import axios from "axios"
import config from "config"
import { queryOptions, useQuery } from "@tanstack/react-query"
import type { QueryClient } from "@tanstack/react-query"
import type { LiveTrainTrail, LiveTreatmentTrain, VehicleSeason } from "types/trains"
import { getFeatureFlagValue } from "utils/featureFlags"

// Live Tracking's queries live here, apart from the page, so the router can
// start them as the route matches (prefetchLiveTracking) while the page's
// chunk downloads; the page then reads them from the cache.

/** Devices report every ~3-5 s: polling every second mostly re-fetched the same rows. */
export const LIVE_TRAINS_POLL_MS = 2500

/** How far back each live train's trail on the map goes. */
const LIVE_TRAIL_INTERVAL = "30 min"

/** The season Live Tracking opens on: the SEASON flag's (Winter unless it says AUTUMN). */
export const defaultLiveTrackingSeason = (): VehicleSeason =>
  getFeatureFlagValue("SEASON") === "AUTUMN" ? "Autumn" : "Winter"

// The API answers "no rows" with { message } rather than []
const asRows = <T,>(data: unknown): T[] => (Array.isArray(data) ? data : [])

export const liveTrainsQueryOptions = (season: VehicleSeason) =>
  queryOptions({
    // season in the key so a page asking for "All" never reads another page's
    // season-filtered cache
    queryKey: ["LiveTrains", season],

    queryFn: async () => {
      const antiCache = Date.now()
      return axios
        .get(`${config.railApiBasePath}/NR/live-trains?season=${season?.toUpperCase()}&anticache=${antiCache}`)
        .then((res) => asRows<LiveTreatmentTrain>(res.data))
    },

    // A prefetched or just-polled result is current enough to mount with
    // (without, the page re-fetched what the router had just fetched).
    // Polling carries on at refetchInterval regardless.
    staleTime: LIVE_TRAINS_POLL_MS,
  })

export const liveLineStringQueryOptions = () =>
  queryOptions({
    queryKey: ["LineString"],

    queryFn: async () => {
      return axios
        .get(
          `${config.railApiBasePath}/NR/rrttm-historical-raw?aslinestring=true&interval=${encodeURIComponent(LIVE_TRAIL_INTERVAL)}`,
        )
        .then((res) => asRows<LiveTrainTrail>(res.data))
    },

    // fetched once per visit (it doesn't poll): a quick revisit reuses it
    staleTime: 60_000,
  })

/** Start Live Tracking's first requests at route match; never rejects. */
export const prefetchLiveTracking = async (queryClient: QueryClient): Promise<void> => {
  await Promise.all([
    queryClient.prefetchQuery(liveTrainsQueryOptions(defaultLiveTrackingSeason())),
    queryClient.prefetchQuery(liveLineStringQueryOptions()),
  ])
}

type Options = {
  /** Live Tracking polls every LIVE_TRAINS_POLL_MS; pages that only need "where is it now" can poll slower. */
  refetchInterval?: number
  enabled?: boolean
}

export function useSIPLiveTrains(season: VehicleSeason, { refetchInterval = LIVE_TRAINS_POLL_MS, enabled = true }: Options = {}) {
  return useQuery({
    ...liveTrainsQueryOptions(season),
    refetchInterval,
    enabled,
  })
}
